import React, { useState, useEffect } from 'react';
import { settingsService } from '../../services/settingsService';
import TemplatePreview from './TemplatePreview';

const TemplateSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resumeTemplates, setResumeTemplates] = useState<string[]>([]);
  const [coverLetterTemplates, setCoverLetterTemplates] = useState<string[]>([]);
  const [selectedResume, setSelectedResume] = useState<string>('');
  const [selectedCoverLetter, setSelectedCoverLetter] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      // Load available templates
      const templatesData = await settingsService.getTemplates();
      setResumeTemplates(templatesData.results.rt);
      setCoverLetterTemplates(templatesData.results.ct);

      // Load user profile to get current selections
      const profile = await settingsService.getProfile();
      if (profile) {
        setSelectedResume(profile.rt || templatesData.results.rt[0]);
        setSelectedCoverLetter(profile.ct || templatesData.results.ct[0]);
      } else {
        // Default to first template if no profile
        setSelectedResume(templatesData.results.rt[0]);
        setSelectedCoverLetter(templatesData.results.ct[0]);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      setError('Failed to load templates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResumeChange = async (template: string) => {
    setSelectedResume(template);
    setSaving(true);
    setError(null);
    try {
      await settingsService.setResumeTemplate(template);
      setShowSuccess('Resume template updated successfully!');
      setTimeout(() => setShowSuccess(null), 3000);
    } catch (error) {
      console.error('Error updating resume template:', error);
      setError('Failed to update resume template. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCoverLetterChange = async (template: string) => {
    setSelectedCoverLetter(template);
    setSaving(true);
    setError(null);
    try {
      await settingsService.setCoverLetterTemplate(template);
      setShowSuccess('Cover letter template updated successfully!');
      setTimeout(() => setShowSuccess(null), 3000);
    } catch (error) {
      console.error('Error updating cover letter template:', error);
      setError('Failed to update cover letter template. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatTemplateName = (name: string): string => {
    return name
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jobesta-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Overlay */}
      {showSuccess && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 rounded-jobesta p-4 shadow-lg z-50 animate-fade-in">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-green-800 font-medium">{showSuccess}</span>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-jobesta p-4">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Resume Template Section */}
      <div className="bg-white rounded-jobesta shadow-jobesta-card p-6 border border-jobesta-gray-40">
        <h2 className="text-lg font-semibold text-jobesta-gray-90 mb-4">Resume Template</h2>
        <p className="text-sm text-jobesta-gray-60 mb-4">
          Select your preferred resume template for document generation.
        </p>

        {/* Resume Preview */}
        {selectedResume && (
          <div className="mb-6">
            <TemplatePreview templateName={selectedResume} templateType="cv" />
          </div>
        )}

        <div className="space-y-2">
          <label className="block text-sm font-medium text-jobesta-gray-80">
            Template
          </label>
          <select
            value={selectedResume}
            onChange={(e) => handleResumeChange(e.target.value)}
            disabled={saving}
            className="w-full md:w-1/2 input-jobesta"
          >
            {resumeTemplates.map((template) => (
              <option key={template} value={template}>
                {formatTemplateName(template)}
              </option>
            ))}
          </select>
          {saving && (
            <p className="text-sm text-jobesta-gray-60 mt-2">
              Saving...
            </p>
          )}
        </div>
      </div>

      {/* Cover Letter Template Section */}
      <div className="bg-white rounded-jobesta shadow-jobesta-card p-6 border border-jobesta-gray-40">
        <h2 className="text-lg font-semibold text-jobesta-gray-90 mb-4">Cover Letter Template</h2>
        <p className="text-sm text-jobesta-gray-60 mb-4">
          Select your preferred cover letter template for document generation.
        </p>

        {/* Cover Letter Preview */}
        {selectedCoverLetter && (
          <div className="mb-6">
            <TemplatePreview templateName={selectedCoverLetter} templateType="cl" />
          </div>
        )}

        <div className="space-y-2">
          <label className="block text-sm font-medium text-jobesta-gray-80">
            Template
          </label>
          <select
            value={selectedCoverLetter}
            onChange={(e) => handleCoverLetterChange(e.target.value)}
            disabled={saving}
            className="w-full md:w-1/2 input-jobesta"
          >
            {coverLetterTemplates.map((template) => (
              <option key={template} value={template}>
                {formatTemplateName(template)}
              </option>
            ))}
          </select>
          {saving && (
            <p className="text-sm text-jobesta-gray-60 mt-2">
              Saving...
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateSettings;
